import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { Table, Button, Modal, Form, Input, Card, Space, Popconfirm, Tooltip, Empty, Row, Col } from "antd";
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  FolderOpenOutlined, 
  EyeOutlined, 
  ArrowLeftOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ShareAltOutlined
} from "@ant-design/icons";
import { message } from "antd";
import { getAllUserFolders, createUserFolder, deleteUserFolder, updateUserFolder, detachProductFromFolder } from "../../actions/user-folders-actions/actions";
import Head from "../Head";
import FolderCatalogView from "./FolderCatalogView";
import "./UserFoldersAdmin.scss";
import { Spinner } from "../Spinner";
import InfiniteScroll from "react-infinite-scroll-component";
import logo from "../../img/logoWithTextBlue.svg";

const UserFoldersAdmin = () => {
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<any>(null);
  const [viewedFolder, setViewedFolder] = useState<any>(null);
  const [withPrices, setWithPrices] = useState(true);
  const [form] = Form.useForm();

  const { folders, isLoading } = useSelector((state: RootStateOrAny) => state.userFolders || { folders: [], isLoading: false });
  const { user, isAdmin } = useSelector((state: RootStateOrAny) => state.auth || { user: null, isAdmin: false });

  const [displayCount, setDisplayCount] = useState(10);
  const foldersToDisplay = useMemo(() => folders?.slice(0, displayCount) || [], [folders, displayCount]);
  const hasMore = displayCount < (folders?.length || 0);

  const loadMoreFolders = () => {
    setTimeout(() => {
      setDisplayCount(prev => prev + 10);
    }, 500);
  };

  useEffect(() => {
    dispatch(getAllUserFolders());
  }, [dispatch]);

  // Update viewed folder if folders list changes
  useEffect(() => {
    if (viewedFolder && folders) {
      const updated = folders.find((f: any) => f.id === viewedFolder.id);
      if (updated) setViewedFolder(updated);
    }
  }, [folders]);

  const showModal = (folder: any = null) => {
    setEditingFolder(folder);
    if (folder) {
      form.setFieldsValue({
        name: folder.name,
        description: folder.description,
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingFolder(null);
    form.resetFields();
  };

  const onFinish = (values: any) => {
    if (editingFolder) {
      dispatch(updateUserFolder(editingFolder.id, values));
    } else {
      dispatch(createUserFolder(values));
    }
    setIsModalVisible(false);
    setEditingFolder(null);
    form.resetFields();
  };

  const handleDelete = (id: any) => {
    dispatch(deleteUserFolder(id));
  };

  const handleDetach = (productId: any) => {
    if (viewedFolder) {
      dispatch(detachProductFromFolder(viewedFolder.id, productId));
    }
  };

  const handlePrintPdf = () => {
    window.print();
  };

  const handleShare = (withPrices: boolean = false) => {
    if (viewedFolder) {
      const encodedId = btoa(viewedFolder.id.toString());
      const shareUrl = `${window.location.origin}/showroom/${encodedId}${withPrices ? '?p=1' : ''}`;
      navigator.clipboard.writeText(shareUrl);
      message.success(`Share link ${withPrices ? 'with prices ' : 'without prices '}copied to clipboard!`);
    }
  };

  const canSeeAll = isAdmin || user?.category === "gold";

  const renderBlurredDetails = (content: any) => {
    if (canSeeAll) return content;

    const isPrice = typeof content === "string" && (content.includes("€") || content.includes("$"));
    const placeholder = isPrice ? "XX.XX €" : "Restricted Information";

    return (
      <span style={{ filter: "blur(5px)", userSelect: "none" }}>
        {placeholder}
      </span>
    );
  };

  const getGenderName = (id: any) => {
    switch (parseInt(id)) {
      case 1: return "MEN";
      case 2: return "WOMEN";
      case 3: return "UNISEX";
      case 4: return "KIDS";
      case 5: return "BOYS";
      case 6: return "GIRLS";
      default: return "";
    }
  };


  if (viewedFolder) {
    return (
      <div className="user_folders_admin_container viewed_folder_mode">
        <Head title={`Viewing ${viewedFolder.name}`} />
        <div className="view_header">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => setViewedFolder(null)}
            className="back_btn"
          >
            Back to Collections
          </Button>
          <div className="header_info">
            <h1>{viewedFolder.name}</h1>
            <p>{viewedFolder.description || "No description provided"}</p>
          </div>
          <Space>
            <Button 
              icon={<ShareAltOutlined />} 
              onClick={() => handleShare(false)}
              className="share_btn"
            >
              Share (No Prices)
            </Button>
            <Button 
              icon={<ShareAltOutlined />} 
              onClick={() => handleShare(true)}
              className="share_btn priced"
            >
              Share (With Prices)
            </Button>
            <Button 
              type="primary" 
              icon={<FilePdfOutlined />} 
              onClick={() => {
                setWithPrices(true);
                setTimeout(() => window.print(), 200);
              }}
              className="pdf_btn priced"
            >
              Catalog with Prices
            </Button>
            <Button 
              type="primary" 
              icon={<FilePdfOutlined />} 
              onClick={() => {
                setWithPrices(false);
                setTimeout(() => window.print(), 200);
              }}
              className="pdf_btn unpriced"
            >
              Catalog without Prices
            </Button>
          </Space>
        </div>

        <Card className="products_grid_card">
          {isLoading && (
            <div className="view_loading_overlay">
              <Spinner />
            </div>
          )}
          {viewedFolder.products && viewedFolder.products.length > 0 ? (
            <Row gutter={[24, 24]}>
              {viewedFolder.products.map((product: any) => (
                <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
                  <div className="admin_product_item_card">
                    <div className="product_img_wrapper">
                      <img 
                        src={process.env.REACT_APP_STORAGE_URL + product.front_image} 
                        alt={product.name} 
                      />
                      <div className="product_overlay">
                        <Popconfirm
                          title="Remove this product from collection?"
                          onConfirm={() => handleDetach(product.id)}
                          okText="Yes"
                          cancelText="No"
                          disabled={isLoading}
                        >
                          <Button 
                            danger 
                            icon={<DeleteOutlined />} 
                            className="detach_btn" 
                            disabled={isLoading}
                          />
                        </Popconfirm>
                      </div>
                    </div>
                    <div className="product_details_mini">
                      <span className="p_code">{product.code}</span>
                      <span className="p_name">{product.name}</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="This collection is empty" />
          )}
        </Card>

        {/* PRINT ONLY SECTION */}
        <FolderCatalogView
          viewedFolder={viewedFolder}
          withPrices={withPrices}
          isAdmin={isAdmin}
          user={user}
        />
      </div>
    );
  }

  return (
    <div className="user_folders_admin_container">
      <Head title="User Folders Management" />
      <Card
        title={
          <div className="card_header">
            <h2>User Collections Management</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
              className="create_btn"
            >
              Create New Collection
            </Button>
          </div>
        }
        bordered={false}
        className="admin_card"
        style={{marginTop: "70px"}}
      >
        {isLoading && folders.length === 0 ? (
          <div className="loading_container">
            <Spinner />
          </div>
        ) : folders && folders.length > 0 ? (
          <div className="infinite_scroll_table_wrapper">
            <div className="table_custom_header">
              <Row>
                <Col span={6}>Name</Col>
                <Col span={4}>Products</Col>
                <Col span={6}>Description</Col>
                <Col span={4}>Created At</Col>
                <Col span={4}>Actions</Col>
              </Row>
            </div>
            
            <InfiniteScroll
              dataLength={foldersToDisplay.length}
              next={loadMoreFolders}
              hasMore={hasMore}
              loader={
                <div className="scroll_loader">
                  <Spinner />
                </div>
              }
              endMessage={
                <div className="scroll_end">
                  <CheckCircleOutlined /> <span>You've seen all collections</span>
                </div>
              }
              height={500}
            >
              {foldersToDisplay.map((record: any) => (
                <div className="table_custom_row" key={record.id}>
                  <Row align="middle">
                    <Col span={6}>
                      <Space>
                        <FolderOpenOutlined style={{ color: "#00468c" }} />
                        <span style={{ fontWeight: 600 }}>{record.name}</span>
                      </Space>
                    </Col>
                    <Col span={4}>
                      {record.products?.length || 0}
                    </Col>
                    <Col span={6} className="ellipsis_col">
                      <Tooltip title={record.description}>
                        {record.description || "-"}
                      </Tooltip>
                    </Col>
                    <Col span={4}>
                      {new Date(record.created_at).toLocaleDateString()}
                    </Col>
                    <Col span={4}>
                      <Space size="middle">
                        <Tooltip title="View Products">
                          <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: "#52c41a" }} />}
                            onClick={() => setViewedFolder(record)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button
                            type="text"
                            icon={<EditOutlined style={{ color: "#00468c" }} />}
                            onClick={() => showModal(record)}
                          />
                        </Tooltip>
                        <Tooltip title="Delete">
                          <Popconfirm
                            title="Are you sure you want to delete this collection?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        </Tooltip>
                      </Space>
                    </Col>
                  </Row>
                </div>
              ))}
            </InfiniteScroll>
          </div>
        ) : (
          <Empty description="No collections found" />
        )}
      </Card>

      <Modal
        title={editingFolder ? "Edit Collection" : "Create New Collection"}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        centered
        className="modern_modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ name: "", description: "" }}
        >
          <Form.Item
            name="name"
            label="Collection Name"
            rules={[{ required: true, message: "Please enter collection name" }]}
          >
            <Input placeholder="e.g. Summer Trends 2026" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Describe the purpose of this collection..." />
          </Form.Item>
          <Form.Item className="modal_footer">
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingFolder ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserFoldersAdmin;
